import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { callAgentProxy } from '../utils/agentClient';
import { getBedrockErrorMessage } from '../utils/bedrockClient';
import type { ChatMessage } from '../utils/aiAgent';
import { FeedbackBar } from './FeedbackBar';
interface Props {
  onError: (msg: string) => void;
}
const STORAGE_KEY = 'ai-chat-wf';
function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
function loadWfHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed.messages) ? parsed.messages : [];
  } catch {
    return [];
  }
}
function saveWfHistory(messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      messages
    }));
  } catch {/* quota exceeded — ignore */}
}
const WORKFRONT_SUGGESTIONS = ['Find opportunity related to Granger Hertzog', 'Find all in progress requests assigned to XYZ  .', 'Recent updates on "XYZ" request', 'Find gnarr of XYZ'];
const GREETING: ChatMessage = {
  id: 'greeting',
  role: 'assistant',
  content: "Hi! I'm the Workfront AI. Ask me about opportunities and requests.",
  timestamp: 0
};
export function WorkfrontAiChat({
  onError
}: Props) {
  const stored = loadWfHistory();
  const [messages, setMessages] = useState<ChatMessage[]>(stored.length > 0 ? stored : [GREETING]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    saveWfHistory(messages);
  }, [messages]);
  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    const userMsg: ChatMessage = {
      id: uid(),
      role: 'user',
      content: text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setError(null);
    setIsStreaming(true);
    setHasUserInteracted(true);
    const assistantId = uid();
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, assistantMsg]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Exclude the static greeting — the user query must be the first message.
      const history = [...messages.filter(m => m.id !== 'greeting'), userMsg];
      const responseText = await callAgentProxy(history, controller.signal);
      setMessages(prev => prev.map(m => m.id === assistantId ? {
        ...m,
        content: responseText || 'No response.'
      } : m));
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = getBedrockErrorMessage(err);
      setError(msg);
      onError(msg);
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [input, isStreaming, messages, onError]);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return <>
      {/* Messages */}
      <div className="ai-messages" data-id="element-1369" data-mp="2ojdg1bhnwhra">
        {messages.map(msg => <div key={msg.id} className={`ai-msg ai-msg-${msg.role}`} data-id="element-1370" data-mp="2avd8g6vx7yi2">
            {msg.role === 'assistant' && <div className="ai-msg-avatar" data-id="element-1371" data-mp="26t8gdcl4f9xj">
                <Sparkles size={12} data-id="element-1372" data-mp="26sgsyp1ogiic" />
              </div>}
            <div className="ai-msg-bubble" data-id="element-1373" data-mp="2kl6ih3gf05k7">
              {msg.role === 'assistant' ? <div className="ai-md" data-id="element-1374" data-mp="2fnwi4srhm9hl">
                  {msg.content ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              a: ({
                children,
                ...props
              }) => <a {...props} target="_blank" rel="noopener noreferrer" data-id="element-1376" data-mp="1nx8ixm9dje6k">{children}</a>
            }} data-id="element-1375" data-mp="1rw90l1g48arz">{msg.content}</ReactMarkdown> : isStreaming && msg.id === messages[messages.length - 1]?.id ? <div className="ai-typing" data-id="element-1377" data-mp="3d9u5b3gtb2zv">
                      <span data-id="element-1378" data-mp="3d8iu4il1esyq" /><span data-id="element-1379" data-mp="3dcgcv33pdii3" /><span data-id="element-1380" data-mp="3dc6m3dau37bc" />
                    </div> : null}
                </div> : <span data-id="element-1381" data-mp="u3rex6wudrcg">{msg.content}</span>}
              {msg.role === 'assistant' && msg.content && <FeedbackBar messageId={msg.id} query={messages.find((m, i) => m.role === 'user' && i < messages.indexOf(msg) && messages[i + 1]?.id === msg.id)?.content} mode="workfront" data-id="element-1382" data-mp="z2umay0ao9nl" />}
            </div>
          </div>)}

        {error && <div className="ai-msg ai-msg-error" data-id="element-1383" data-mp="1dsa9k40m9wyj">
            <span data-id="element-1384" data-mp="1htlrroh5vfwp">{error}</span>
          </div>}

        {!isStreaming && !hasUserInteracted && !error && !input && <div className="ai-suggestions" data-id="element-1385" data-mp="140w6xnddch16">
            {WORKFRONT_SUGGESTIONS.map(s => <button key={s} className="ai-suggestion-chip" onClick={() => {
          setInput(s);
          inputRef.current?.focus();
        }} data-id="element-1386" data-mp="18zh75msmf0pm">
                {s}
              </button>)}
          </div>}

        <div ref={messagesEndRef} data-id="element-1387" data-mp="2bca7xpcl91ju" />
      </div>

      {/* Input */}
      <div className="ai-input-bar" data-id="element-1388" data-mp="26dp31cme5tp2">
        <textarea ref={inputRef} className="ai-input" placeholder="Ask anything about your deals..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} rows={1} disabled={isStreaming} data-id="element-1389" data-mp="26eqli25qligt" />
        <button className="ai-send-btn" onClick={() => void handleSend()} disabled={!input.trim() || isStreaming} title="Send message" data-id="element-1390" data-mp="1sgabo2ckvbug">
          {isStreaming ? <Loader2 size={16} className="ai-spin" data-id="element-1391" data-mp="1ima13kyj1eec" /> : <Send size={16} data-id="element-1392" data-mp="1dpt9uhw16jx1" />}
        </button>
      </div>
    </>;
}
export function clearWorkfrontChatHistory(): void {
  localStorage.removeItem(STORAGE_KEY);
}